import {Template} from '@dv5/domain'
import React, {FC} from 'react'
import {JsonEditor} from './JsonEditor'
import {JsonTemplateView} from './JsonTemplateView'
import {DocumentPartTemplateContent} from './DocumentPartTemplateContent'
import {DocumentTemplateContent} from './DocumentTemplateContent'
import {TaskTemplateContent} from './TaskTemplateContent'
import {TemplateDataFooter} from './TemplateDataFooter'
import {PlaceholderLegend} from './PlaceholderLegend'
import {useTemplateDataEditor} from './useTemplateDataEditor'
import './TemplateDataDialog.less'

interface Props {
    template: Template
    onClose: () => void
    onTemplateUpdated?: (template: Template) => void
}

export const TemplateDataDialog: FC<Props> = ({template, onClose, onTemplateUpdated}) => {
    const editor = useTemplateDataEditor({template, onClose, onTemplateUpdated})

    const renderContent = () => {
        if (editor.viewMode === 'jsonEdit') {
            return <JsonEditor value={editor.draftJson} onChange={editor.setDraftJson} />
        }

        if (editor.docPart) {
            return (
                <DocumentPartTemplateContent
                    initialFormData={editor.initialFormData}
                    formPartHeader={editor.formPartHeader}
                    formPartContent={editor.formPartContent}
                    onHeaderChange={editor.setFormPartHeader}
                    onContentChange={editor.setFormPartContent}
                    viewMode={editor.viewMode}
                />
            )
        }

        if (editor.task) {
            return (
                <TaskTemplateContent
                    initialTaskFormData={editor.initialTaskFormData}
                    formTaskData={editor.formTaskData}
                    onTaskDataChange={editor.setFormTaskData}
                    onConfirm={editor.handleSaveFromTaskForm}
                    onCancel={editor.cancelEdit}
                    viewMode={editor.viewMode}
                />
            )
        }

        if (editor.docTemplate) {
            return (
                <DocumentTemplateContent
                    initialFormData={editor.initialDocTemplateFormData}
                    formData={editor.docTemplateFormData}
                    onTitleChange={editor.setDocTemplateTitle}
                    onPartChange={editor.handleDocTemplatePartChange}
                    onAddPart={editor.handleDocTemplateAddPart}
                    onRemovePart={editor.handleDocTemplateRemovePart}
                    onMovePart={editor.handleDocTemplateMovePart}
                    onConfirm={editor.handleSaveFromDocumentForm}
                    onCancel={editor.cancelEdit}
                    viewMode={editor.viewMode}
                />
            )
        }

        return <JsonTemplateView data={editor.displayTemplate.templateData} />
    }

    return (
        <div className="template-data-dialog-overlay" onMouseDown={editor.handleOverlayClick}>
            <div className="template-data-dialog" ref={editor.dialogRef}>
                <div className="template-data-header">
                    <h3 className="template-data-title">{editor.displayTemplate.title}</h3>
                    <span className="template-data-type">{editor.displayTemplate.templateType}</span>
                </div>
                <div className={`template-data-body${editor.viewMode === 'jsonEdit' ? ' editing' : ''}`}>
                    {editor.viewMode === 'formEdit' && (
                        <PlaceholderLegend data={editor.displayTemplate.templateData} />
                    )}
                    {renderContent()}
                </div>
                {editor.errorMessage && (
                    <div className="error-message">{editor.errorMessage}</div>
                )}
                <div className="template-data-footer">
                    <TemplateDataFooter
                        hasFormEditor={editor.hasFormEditor}
                        viewMode={editor.viewMode}
                        isSaving={editor.isSaving}
                        onEdit={editor.hasFormEditor ? editor.enterFormEdit : editor.enterJsonEdit}
                        onClose={onClose}
                        onCancel={editor.cancelEdit}
                        onSwitchToForm={editor.switchToForm}
                        onSwitchToJson={editor.enterJsonEdit}
                        onSaveJson={editor.handleSaveFromJson}
                        onSaveForm={editor.docPart ? editor.handleSaveFromForm : editor.task ? editor.handleSaveFromTaskForm : editor.handleSaveFromDocumentForm}
                    />
                </div>
            </div>
        </div>
    )
}
